import React, { useRef, useImperativeHandle } from "react";
import { noop } from "lodash";
import cn from "../../../utils/cn";
import Icon from "../Icon/Icon";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Button = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    id = "",
    className = "",
    label = "",
    link = false,
    onClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    primary = false,
    secondary = false,
    destructive = false,
    outline = false,
    disabled = false,
    fullWidth = false,
    shouldRender = true,
    style = {},
    icon = null,
    type = "button",
    "data-test-element": dataTestElement = null,
    "data-testid": dataTestId = null,
    upperCase = false,
    ariaHasPopUp,
    omitDefaultStyles = false,
    ariaExpanded,
    ...rest
  } = props;
  const buttonRef = useRef(null);
  const handleClick = e => {
    if (disabled) {
      e.preventDefault();
    } else {
      onClick(e);
      buttonRef.current?.blur();
    }
  };
  useImperativeHandle(ref, () => buttonRef.current);
  const buttonClass = cn("inline-block font-regular-font py-2 px-4 rounded border border-solid focus:outline-solid-offset",
  // Base classes
  {
    // Conditional classes
    "text-sm font-heavy-font focus:border-focus-blue normal-case": !omitDefaultStyles,
    "bg-action-blue text-white border-transparent": primary && !omitDefaultStyles,
    "hover:bg-deep-blue": primary && !omitDefaultStyles && !disabled,
    "bg-white text-brand-blue border-light-border-blue": secondary && !omitDefaultStyles,
    "hover:bg-medium-fill-grey": secondary && !omitDefaultStyles && !disabled,
    "bg-transparent text-action-blue border-medium-border-blue": outline && !omitDefaultStyles,
    "hover:border-action-blue hover:text-deep-blue": outline && !omitDefaultStyles && !disabled,
    "bg-white border-button text-black": !primary && !secondary && !outline && !link && !omitDefaultStyles,
    "border-none bg-transparent text-action-blue": link,
    "bg-red text-white border-transparent": !primary && !secondary && !outline && destructive && !omitDefaultStyles,
    "hover:bg-dark-red": !primary && !secondary && !outline && destructive && !disabled && !omitDefaultStyles,
    "opacity-40 cursor-default": disabled,
    "cursor-pointer": !disabled,
    "w-full": fullWidth,
    uppercase: upperCase,
    "flex items-center justify-center": icon
  }, className // User-defined classes
  );
  const iconElement = icon?.name ? /*#__PURE__*/_jsx(Icon, {
    color: icon?.color,
    icon: icon.name,
    style: icon?.style,
    className: icon?.className,
    direction: icon?.direction
  }) : null;
  if (!shouldRender) {
    return null;
  }
  return /*#__PURE__*/_jsxs("button", {
    id: id,
    ref: buttonRef,
    className: buttonClass,
    "aria-haspopup": ariaHasPopUp,
    "aria-label": label,
    onClick: handleClick,
    onMouseLeave: onMouseLeave,
    onMouseEnter: onMouseEnter,
    "aria-disabled": disabled,
    "aria-expanded": ariaExpanded,
    style: style,
    type: type,
    "data-test-element": dataTestElement,
    "data-testid": dataTestId,
    ...rest,
    children: [iconElement && icon?.position === "left" && /*#__PURE__*/_jsx("span", {
      style: {
        color: 'inherit'
      },
      className: "inline-block mr-2 transform -translate-y-px",
      "aria-hidden": "true",
      children: iconElement
    }), label, iconElement && icon?.position === "right" && /*#__PURE__*/_jsx("span", {
      style: {
        color: 'inherit'
      },
      className: "inline-block ml-2 transform -translate-y-px",
      "aria-hidden": "true",
      children: iconElement
    })]
  });
});
Button.displayName = 'Button';
export default Button;
//# sourceMappingURL=Button.js.map